import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { CompanySelect, CreatePage, FuseInput, FuseSelect, InvestorParticipations } from '/src/components';
import { useFilterStore, useLoanStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { CreateLoanForm, DataModelType, FormErrors, LoanStatus, LoanType, PaymentMethod } from '/src/lib/types';
import { isNumber } from '/src/utils';

/**
 * Create Loan Page
 */
export const CreateLoanPage = observer(() => {
  /** Context **/
  const loanStore = useLoanStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    true, // Lender Participations
  ]);

  const [formValues, setFormValues] = useState<CreateLoanForm>({
    name: '',
    isOwned: false,
    amount: 0,
    interestRate: 0,
    advanceRate: 0,
    originationFee: 0,
    term: 0,
    minimumPayment: 0,
    status: LoanStatus.Active,
    socketComments: [],
    companyComments: [],
    files: [],
    principalDateUpdated: new Date(),
    lenders: [],
    adminFee: 0,
    discountFee: 0,
    payDownPercent: 0,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const dealType = useMemo(() => (formValues?.isOwned ? LoanType.Owned : LoanType.Loan), [formValues?.isOwned]);
  const companies = useMemo(() => filterStore.companyListGet(), [filterStore._companyList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key]) {
      setFormErrors((prev) => ({
        ...prev,
        [key]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      name: 'Deal Name is required',
      dealType: 'Deal Type is required',
      borrower: 'Borrower is required',
      amount: 'Valid amount is required',
      advanceRate: 'Advance Rate is required',
      term: 'Term is required',
      maturityDate: 'Maturity Date is required',
      lastAdvanceDate: 'Last Advance Date is required',
      interestRate: 'Interest Rate is required',
      originationFee: 'Origination Fee is required',
      minimumPayment: 'Minimum Payment is required',
      payDownPercent: 'Pay Down Percent is required',
      minPmtMethod: 'Minimum Payment Method is required',
      adminFee: 'Admin Fee is required',
      discountFee: 'Discount Fee is required',
    };

    if (!formValues?.['name']) {
      errors['name'] = errorMessages['name'];
    }
    if (typeof formValues?.['isOwned'] !== 'boolean') {
      errors['dealType'] = errorMessages['dealType'];
    }
    if (!formValues?.['borrower']) {
      errors['borrower'] = errorMessages['borrower'];
    }
    if (!formValues?.['amount'] || !isNumber(formValues?.['amount'])) {
      errors['amount'] = errorMessages['amount'];
    }
    if (!isNumber(formValues?.['advanceRate'])) {
      errors['advanceRate'] = errorMessages['advanceRate'];
    }

    if (dealType === LoanType.Loan) {
      if (!formValues?.['maturityDate']) {
        errors['maturityDate'] = errorMessages['maturityDate'];
      }
      if (!formValues?.['lastAdvanceDate']) {
        errors['lastAdvanceDate'] = errorMessages['lastAdvanceDate'];
      }
      if (!formValues?.['term']) {
        errors['term'] = errorMessages['term'];
      }
      if (!isNumber(formValues?.['interestRate'])) {
        errors['interestRate'] = errorMessages['interestRate'];
      }
      if (!isNumber(formValues?.['originationFee'])) {
        errors['originationFee'] = errorMessages['originationFee'];
      }
      if (formValues?.['minPmtMethod'] === PaymentMethod.SetAmount && !formValues?.['minimumPayment']) {
        errors['minimumPayment'] = errorMessages['minimumPayment'];
      }
      if (
        formValues?.['minPmtMethod'] === PaymentMethod.PayDown &&
        dealType === LoanType.Loan &&
        !isNumber(formValues?.['payDownPercent'])
      ) {
        errors['payDownPercent'] = errorMessages['payDownPercent'];
      }
      if (!formValues?.['minPmtMethod']) {
        errors['minPmtMethod'] = errorMessages['minPmtMethod'];
      }
    } else if (dealType === LoanType.Owned) {
      if (!isNumber(formValues?.['adminFee'])) {
        errors['adminFee'] = errorMessages['adminFee'];
      }
      if (!isNumber(formValues?.['discountFee'])) {
        errors['discountFee'] = errorMessages['discountFee'];
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates a Loan.
   *
   * @remarks
   * Redirects to the new Loan's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await loanStore.create(formValues);
      if (!response) throw new Error(`Could not create Loan with data: ${JSON.stringify(formValues)}`);

      toast.success(toastMessages.createLoan.success);
      navigate(`/deals/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.createLoan.error);
    }
  };

  /** Render **/
  return (
    <CreatePage
      modelType={DataModelType.Loan}
      onCreate={create}
      sidebarFlex="40"
      contentFlex="60"
      sidebar={
        <InvestorParticipations
          open={sidebarItems[0]}
          toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
          participations={formValues.lenders}
          setParticipations={updateFormValue('lenders')}
        />
      }
    >
      {/* Borrower */}
      <CompanySelect
        id="company"
        value={formValues?.borrower ?? ''}
        setValue={updateFormValue('borrower')}
        companies={companies}
        required
        error={formErrors.borrower}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Deal Name */}
        <Box display="flex" flex={70}>
          <FuseInput
            label="Deal Name"
            value={formValues?.name}
            updateValue={updateFormValue('name')}
            required
            error={formErrors.name}
          />
        </Box>

        {/* Deal Type */}
        <Box display="flex" flex={30}>
          <FuseSelect
            label="Deal Type"
            options={Object.values(LoanType)}
            value={dealType}
            updateValue={(dealType) => updateFormValue('isOwned')(dealType === LoanType.Owned)}
            placeholder="Deal Type"
            required
            tooltip="Deal type cannot be changed after the Deal is created"
            error={formErrors.dealType}
          />
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Amount */}
        <FuseInput
          label="Amount"
          value={formValues?.amount}
          tooltip={'Total indebtedness including principal and outstanding interest charges ("Commitment Amount")'}
          updateValue={updateFormValue('amount')}
          min="0"
          moneyMask
          required
          error={formErrors.amount}
        />
        {/* Advance Rate */}
        <FuseInput
          label="Advance Rate"
          tooltip="Percent of the value of eligible assets that can be made available for advances"
          value={formValues?.advanceRate}
          updateValue={updateFormValue('advanceRate')}
          min="0"
          percentageMask
          required
          error={formErrors.advanceRate}
        />
      </Box>

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Minimum Payment Method */}
          <FuseSelect
            label="Min. Monthly Pmt. Method"
            options={Object.values(PaymentMethod)}
            value={formValues?.minPmtMethod}
            updateValue={updateFormValue('minPmtMethod')}
            placeholder="Select Method..."
            error={formErrors.minPmtMethod}
          />

          {/* Minimum Payment */}
          {formValues.minPmtMethod === PaymentMethod.SetAmount && (
            <FuseInput
              label="Minimum Payment"
              value={formValues?.minimumPayment}
              updateValue={updateFormValue('minimumPayment')}
              min="0"
              moneyMask
              error={formErrors.minimumPayment}
            />
          )}

          {/* Pay Down Percent */}
          {formValues.minPmtMethod === PaymentMethod.PayDown && (
            <FuseInput
              label="Pay Down Percent"
              value={formValues?.payDownPercent}
              updateValue={updateFormValue('payDownPercent')}
              min="0"
              percentageMask
              error={formErrors.payDownPercent}
            />
          )}

          {/* Term */}
          <FuseInput
            label="Term (Months)"
            value={formValues?.term}
            updateValue={updateFormValue('term')}
            type="number"
            min="0"
            required={dealType === LoanType.Loan}
            error={formErrors.term}
          />
        </Box>
      )}

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Maturity Date */}
          <FuseInput
            label="Maturity Date"
            value={formValues?.maturityDate}
            updateValue={updateFormValue('maturityDate')}
            type="date"
            required
            error={formErrors.maturityDate}
          />

          {/* Last Advance Date */}
          <FuseInput
            label="Last Advance Date"
            value={formValues?.lastAdvanceDate}
            updateValue={updateFormValue('lastAdvanceDate')}
            type="date"
            required
            error={formErrors.lastAdvanceDate}
          />
        </Box>
      )}

      {dealType === LoanType.Loan && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Origination Fee */}
          <FuseInput
            label="Origination Fee"
            tooltip="Percentage fee charged for each advance"
            value={formValues?.originationFee}
            updateValue={updateFormValue('originationFee')}
            min="0"
            percentageMask
            required
            error={formErrors.originationFee}
          />

          {/* Monthly Interest Rate */}
          <FuseInput
            label="Monthly Interest Rate"
            tooltip="Applied daily to the outstanding principal balance"
            value={formValues?.interestRate}
            updateValue={updateFormValue('interestRate')}
            min="0"
            percentageMask
            required
            error={formErrors.interestRate}
          />
        </Box>
      )}

      {dealType === LoanType.Owned && (
        <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
          {/* Administrative Fee % */}
          <FuseInput
            label="Administrative Fee"
            value={formValues?.adminFee}
            updateValue={updateFormValue('adminFee')}
            min="0"
            percentageMask
            required
            error={formErrors.adminFee}
          />

          {/* Discount Fee % */}
          <FuseInput
            label="Discount Fee"
            value={formValues?.discountFee}
            updateValue={updateFormValue('discountFee')}
            min="0"
            percentageMask
            required
            error={formErrors.discountFee}
          />
        </Box>
      )}
    </CreatePage>
  );
});
